import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useWithdrawals } from "../../contexts/WithdrawalContext";
import { WithdrawalStatus, WithdrawalMethod, WithdrawalStatusLabels } from "../../types/payment";
import { Button } from "../ui/Button";
import { Trash2, X, Clock, CheckCircle, AlertCircle, XCircle, Download, Loader2 } from "lucide-react";
import { ReceiptGenerator, createWithdrawalReceiptData } from "../../utils/receiptGenerator";
import { getDynamicCurrencySymbol } from "../../lib/currencyUtils";

const WithdrawalsList: React.FC = () => {
  const { t } = useTranslation();
  const { withdrawals, cancelWithdrawal, deleteWithdrawal, loading } = useWithdrawals();
  const [downloadingReceipts, setDownloadingReceipts] = useState<Set<string>>(new Set());

  const getStatusText = (status: number | null) => {
    if (status === null) return t("walletDashboard.statusLabels.unknown");
    return WithdrawalStatusLabels[status as keyof typeof WithdrawalStatusLabels] || t("walletDashboard.statusLabels.unknown");
  };

  const getStatusColor = (status: number | null) => {
    switch (status) {
      case WithdrawalStatus.PENDING:
        return "bg-yellow-100 text-yellow-800";
      case WithdrawalStatus.PROCESSING:
        return "bg-blue-100 text-blue-800";
      case WithdrawalStatus.COMPLETED:
        return "bg-green-100 text-green-800";
      case WithdrawalStatus.FAILED:
        return "bg-red-100 text-red-800";
      case WithdrawalStatus.CANCELLED:
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusIcon = (status: number | null) => {
    switch (status) {
      case WithdrawalStatus.PENDING:
        return <Clock className="h-4 w-4" />;
      case WithdrawalStatus.PROCESSING:
        return <Clock className="h-4 w-4" />;
      case WithdrawalStatus.COMPLETED:
        return <CheckCircle className="h-4 w-4" />;
      case WithdrawalStatus.FAILED:
        return <XCircle className="h-4 w-4" />;
      case WithdrawalStatus.CANCELLED:
        return <X className="h-4 w-4" />;
      default:
        return <AlertCircle className="h-4 w-4" />;
    }
  };

  const handleCancel = async (id: string) => {
    if (window.confirm(t("withdrawalsList.confirmCancel"))) {
      try {
        await cancelWithdrawal(id);
      } catch (error) {
        alert(t("withdrawalsList.cancelFailed"));
      }
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm(t("withdrawalsList.confirmDelete"))) {
      try {
        await deleteWithdrawal(id);
      } catch (error) {
        alert(t("withdrawalsList.deleteFailed"));
      }
    }
  };

  const downloadWithdrawalReceipt = async (withdrawal: any) => {
    if (!withdrawal) {
      alert(t("withdrawalsList.noReceiptData"));
      return;
    }

    const withdrawalId = withdrawal.id;
    
    try {
      // Add to downloading set
      setDownloadingReceipts(prev => new Set(prev).add(withdrawalId));
      
      const receiptGenerator = new ReceiptGenerator();
      const receiptData = createWithdrawalReceiptData(withdrawal);
      
      // Simulate a small delay to show loading state
      await new Promise(resolve => setTimeout(resolve, 500));
      
      receiptGenerator.generateReceipt(receiptData);
    } catch (error) {
      console.error("Error generating withdrawal receipt:", error);
      alert(t("withdrawalsList.receiptGenerateFailed"));
    } finally {
      // Remove from downloading set
      setDownloadingReceipts(prev => {
        const newSet = new Set(prev);
        newSet.delete(withdrawalId);
        return newSet;
      });
    }
  };

  const getPaymentMethodDisplayName = (paymentMethod: any) => {
    if (paymentMethod.type === "card") {
      return t("withdrawalsList.paymentMethods.card", { brand: paymentMethod.brand?.toUpperCase(), last4: paymentMethod.last4 });
    } else if (paymentMethod.type === "bank_account" || paymentMethod.type === "us_bank_account") {
      return t("withdrawalsList.paymentMethods.bankAccount", { bankName: paymentMethod.bankName, last4: paymentMethod.last4 });
    } else if (paymentMethod.type === "paypal") {
      return t("withdrawalsList.paymentMethods.paypal", { email: paymentMethod.paypalEmail });
    }
    return t("withdrawalsList.unknownPaymentMethod");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="flex items-center gap-2">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          <span className="text-gray-600">{t("withdrawalsList.loading")}</span>
        </div>
      </div>
    );
  }

  if (withdrawals.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="text-gray-400 mb-4">
          <Clock className="h-12 w-12 mx-auto" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">{t("withdrawalsList.noWithdrawals")}</h3>
        <p className="text-gray-600">{t("withdrawalsList.noWithdrawalsDesc")}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {withdrawals.map((withdrawal) => (
        <div key={withdrawal.id} className="border rounded-lg p-4 bg-white">
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <div className={`p-2 rounded-full ${getStatusColor(withdrawal.status)}`}>
                  {getStatusIcon(withdrawal.status)}
                </div>
                <div>
                  <div className="font-medium text-lg text-red-600">
                    -{getDynamicCurrencySymbol()}{withdrawal.amount?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                  <div className="text-sm text-gray-500">
                    {withdrawal.paymentMethod ? getPaymentMethodDisplayName(withdrawal.paymentMethod) : "Unknown Payment Method"}
                  </div>
                </div>
              </div>
              
              {withdrawal.notes && (
                <div className="text-sm text-gray-600 mt-2">
                  <strong>{t("withdrawalsList.notes")}:</strong> {withdrawal.notes}
                </div>
              )}
              
              {withdrawal.failureReason && (
                <div className="text-sm text-red-600 mt-2 bg-red-50 p-2 rounded">
                  <strong>{t("withdrawalsList.error")}:</strong> {withdrawal.failureReason}
                </div>
              )}
              
              <div className="text-xs text-gray-500 mt-2">
                <div>{t("withdrawalsList.created")}: {new Date(withdrawal.createdAt).toLocaleDateString()}</div>
                {withdrawal.processedAt && (
                  <div>{t("withdrawalsList.processed")}: {new Date(withdrawal.processedAt).toLocaleDateString()}</div>
                )}
                {withdrawal.completedAt && (
                  <div>{t("withdrawalsList.completed")}: {new Date(withdrawal.completedAt).toLocaleDateString()}</div>
                )}
              </div>
            </div>
            
            <div className="flex flex-col items-end gap-2">
              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(withdrawal.status)}`}>
                {getStatusIcon(withdrawal.status)}
                {getStatusText(withdrawal.status)}
              </span>
              
              <div className="flex gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => downloadWithdrawalReceipt(withdrawal)}
                  disabled={downloadingReceipts.has(withdrawal.id)}
                  className="text-blue-600 hover:text-blue-700 flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {downloadingReceipts.has(withdrawal.id) ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {t("withdrawalsList.generating")}
                    </>
                  ) : (
                    <>
                      <Download className="h-4 w-4" />
                      {t("withdrawalsList.receipt")}
                    </>
                  )}
                </Button>
                
                {withdrawal.status === WithdrawalStatus.PENDING && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleCancel(withdrawal.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    {t("withdrawalsList.cancel")}
                  </Button>
                )}
                
                {(withdrawal.status === WithdrawalStatus.CANCELLED || 
                  withdrawal.status === WithdrawalStatus.FAILED) && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(withdrawal.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default WithdrawalsList;
